import { useEffect } from 'react'
import type { CSSProperties } from 'react'
import { Button, SegmentedControl, SelectField, TextField } from '@shared/design-system/ui'
import {
  DEFAULT_UPSELL_WIDGET_STYLE,
  hasReadableUpsellButtonContrast,
} from '@entities/product/api/product.contract'
import type { UpsellWidgetStyle } from '@entities/product/api/product.contract'
import {
  widgetPreviewBadge,
  widgetPreviewButton,
  widgetPreviewDecline,
  widgetPreviewFrame,
  widgetPreviewPrice,
  widgetPreviewTitle,
  widgetPreviewTrust,
  widgetStyleBody,
  widgetStyleColors,
  widgetStyleControlLabel,
  widgetStyleControls,
  widgetStyleHeader,
  widgetStyleSection,
} from './upsell-widget-style.css'
import { sectionCopy, sectionTitle } from './product.css'

type Font = UpsellWidgetStyle['font']

const FONT_FAMILIES: Record<Font, { label: string; family: string; weights: string }> = {
  inter: { label: 'Inter', family: 'Inter', weights: '400;600' },
  roboto: { label: 'Roboto', family: 'Roboto', weights: '400;500' },
  'open-sans': { label: 'Open Sans', family: 'Open Sans', weights: '400;600' },
  montserrat: { label: 'Montserrat', family: 'Montserrat', weights: '400;600' },
  poppins: { label: 'Poppins', family: 'Poppins', weights: '400;600' },
  lato: { label: 'Lato', family: 'Lato', weights: '400;700' },
}
const RADIUS_PX: Record<UpsellWidgetStyle['radius'], string> = {
  square: '0px',
  soft: '10px',
  rounded: '16px',
  pill: '999px',
}
const TEXT_SIZE_PX: Record<UpsellWidgetStyle['textSize'], string> = {
  sm: '14px',
  md: '16px',
  lg: '18px',
}

export const upsellButtonContrastMessage =
  'O texto do botão precisa de mais contraste com a cor do botão para ficar legível.'

function ensurePreviewFont(font: Font) {
  const id = `upsell-widget-font-${font}`
  if (document.getElementById(id) !== null) return
  const { family, weights } = FONT_FAMILIES[font]
  const link = document.createElement('link')
  link.id = id
  link.rel = 'stylesheet'
  link.href = `https://fonts.googleapis.com/css2?family=${family.replace(/ /g, '+')}:wght@${weights}&display=swap`
  document.head.appendChild(link)
}

export function UpsellWidgetStyleEditor({
  value,
  previewTitle,
  previewPrice,
  previewAcceptLabel,
  previewRejectLabel,
  error,
  onChange,
}: {
  value: UpsellWidgetStyle | null | undefined
  previewTitle: string
  previewPrice: string
  previewAcceptLabel: string
  previewRejectLabel: string
  error?: string | undefined
  onChange: (style: UpsellWidgetStyle | null) => void
}) {
  const style = value ?? DEFAULT_UPSELL_WIDGET_STYLE
  const contrastError = hasReadableUpsellButtonContrast(style.buttonColor, style.buttonTextColor)
    ? undefined
    : upsellButtonContrastMessage

  useEffect(() => {
    ensurePreviewFont(style.font)
  }, [style.font])

  function update(changes: Partial<UpsellWidgetStyle>) {
    onChange({ ...style, ...changes })
  }

  const buttonStyle: CSSProperties = {
    background: style.buttonColor,
    color: style.buttonTextColor,
    borderRadius: RADIUS_PX[style.radius],
    fontFamily: `'${FONT_FAMILIES[style.font].family}', sans-serif`,
    fontSize: TEXT_SIZE_PX[style.textSize],
  }

  return (
    <section className={widgetStyleSection} data-testid="product-upsell-widget-style">
      <div className={widgetStyleHeader}>
        <div>
          <h2 className={sectionTitle}>Visual das ofertas pós-compra</h2>
          <p className={sectionCopy}>
            Vale para todos os upsells e downsells exibidos em &quot;Minha página&quot;. O widget
            não copia mais o estilo da sua página. Título, preço e selos usam as cores do tema para
            continuar legíveis.
          </p>
        </div>
        <Button
          variant="secondary"
          onClick={() => onChange(null)}
          data-testid="product-upsell-widget-reset-btn"
        >
          Usar padrão VendePay
        </Button>
      </div>
      <div className={widgetStyleBody}>
        <div className={widgetStyleControls}>
          <div data-testid="product-upsell-widget-theme">
            <span className={widgetStyleControlLabel}>Tema</span>
            <SegmentedControl
              ariaLabel="Tema do widget"
              value={style.theme}
              options={[
                { value: 'light', label: 'Claro' },
                { value: 'dark', label: 'Escuro' },
              ]}
              onChange={(theme) => update({ theme })}
            />
          </div>
          <div className={widgetStyleColors}>
            <TextField
              label="Cor do botão"
              type="color"
              value={style.buttonColor}
              onChange={(event) => update({ buttonColor: event.target.value.toUpperCase() })}
              data-testid="product-upsell-widget-button-color"
            />
            <TextField
              id="field-upsellWidgetStyle.buttonTextColor"
              label="Cor do texto do botão"
              type="color"
              value={style.buttonTextColor}
              error={error ?? contrastError}
              onChange={(event) => update({ buttonTextColor: event.target.value.toUpperCase() })}
              data-testid="product-upsell-widget-text-color"
            />
          </div>
          <div data-testid="product-upsell-widget-radius">
            <span className={widgetStyleControlLabel}>Arredondamento do botão</span>
            <SegmentedControl
              ariaLabel="Arredondamento do botão"
              value={style.radius}
              options={[
                { value: 'square', label: 'Reto' },
                { value: 'soft', label: 'Suave' },
                { value: 'rounded', label: 'Arredondado' },
                { value: 'pill', label: 'Pílula' },
              ]}
              onChange={(radius) => update({ radius })}
            />
          </div>
          <SelectField
            label="Fonte do botão"
            value={style.font}
            options={(Object.keys(FONT_FAMILIES) as Font[]).map((font) => ({
              value: font,
              label: FONT_FAMILIES[font].label,
            }))}
            onChange={(event) => update({ font: event.target.value as Font })}
            data-testid="product-upsell-widget-font"
          />
          <div data-testid="product-upsell-widget-text-size">
            <span className={widgetStyleControlLabel}>Tamanho do texto do botão</span>
            <SegmentedControl
              ariaLabel="Tamanho do texto do botão"
              value={style.textSize}
              options={[
                { value: 'sm', label: 'P' },
                { value: 'md', label: 'M' },
                { value: 'lg', label: 'G' },
              ]}
              onChange={(textSize) => update({ textSize })}
            />
          </div>
        </div>
        <div>
          <span className={widgetStyleControlLabel}>Prévia</span>
          <div
            className={widgetPreviewFrame({ theme: style.theme })}
            data-testid="product-upsell-widget-preview"
            aria-hidden="true"
          >
            <span className={widgetPreviewBadge}>Oferta exclusiva por tempo limitado</span>
            <strong className={widgetPreviewTitle}>{previewTitle}</strong>
            <span className={widgetPreviewPrice}>{previewPrice}</span>
            <span className={widgetPreviewButton} style={buttonStyle}>
              {previewAcceptLabel}
            </span>
            <span className={widgetPreviewDecline({ theme: style.theme })}>
              {previewRejectLabel}
            </span>
            <span className={widgetPreviewTrust}>Compra com 1 clique e segura</span>
          </div>
        </div>
      </div>
    </section>
  )
}
